import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Spin } from 'antd';
import { CloseOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import http from '../../store/services/http-common';
import useTranslation from '../../localization/useTranslation';
import appToast from '../../utilities/appToast';
import { getStoredUser, isAuthenticated } from '../../utilities/authentication';
import { SUPPORT_CHAT_OPEN_EVENT } from '../../utilities/supportChat';
import { queryKeys } from '../../query/queryKeys';
import './SupportChatWidget.css';

const UNREAD_POLL_MS = 30000;
const THREAD_POLL_MS = 5000;
const MAX_BODY_LENGTH = 2000;

async function fetchSupportUnreadCount() {
  const { data } = await http.get('/support/unread-count');
  return Number(data?.data?.unread_count ?? 0);
}

async function fetchSupportThread(afterId) {
  const { data } = await http.get('/support/conversation', {
    params: { after_id: afterId || undefined, mark_read: 1 },
  });
  return data?.data ?? { conversation: null, messages: [] };
}

const SupportChatWidget = () => {
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const authenticated = isAuthenticated();
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const lastIdRef = useRef(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const unreadQuery = useQuery({
    queryKey: queryKeys.support.unreadCount(),
    queryFn: fetchSupportUnreadCount,
    refetchInterval: UNREAD_POLL_MS,
    staleTime: UNREAD_POLL_MS,
    enabled: authenticated && !open,
  });
  const unreadCount = open ? 0 : unreadQuery.data ?? 0;

  const mergeMessages = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      return fresh.length ? [...prev, ...fresh].sort((a, b) => a.id - b.id) : prev;
    });
  }, []);

  const applyThread = useCallback((thread) => {
    const incoming = thread?.messages ?? [];
    setConversation(thread?.conversation ?? null);
    mergeMessages(incoming);
    if (incoming.length) {
      lastIdRef.current = Math.max(lastIdRef.current, ...incoming.map((m) => m.id));
    }
    queryClient.setQueryData(queryKeys.support.unreadCount(), 0);
  }, [mergeMessages, queryClient]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SUPPORT_CHAT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const load = () => fetchSupportThread(lastIdRef.current)
      .then((thread) => { if (!cancelled) applyThread(thread); })
      .catch(() => {});

    setLoading(lastIdRef.current === 0);
    load().finally(() => { if (!cancelled) setLoading(false); });
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, THREAD_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, applyThread]);

  useEffect(() => {
    if (!open) return;

    if (typeof window !== 'undefined' && !window.matchMedia('(max-width: 575px)').matches) {
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.matchMedia('(max-width: 575px)').matches) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.matchMedia('(max-width: 575px)').matches) {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    const setViewportHeight = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      rootStyle.setProperty('--support-chat-vh', `${Math.round(height)}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.visualViewport?.addEventListener('resize', setViewportHeight);
    window.visualViewport?.addEventListener('scroll', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.visualViewport?.removeEventListener('resize', setViewportHeight);
      window.visualViewport?.removeEventListener('scroll', setViewportHeight);
      rootStyle.removeProperty('--support-chat-vh');
    };
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [open, messages.length, loading]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await http.post('/support/messages', { body });
      mergeMessages([data?.data?.message].filter(Boolean));
      setConversation(data?.data?.conversation ?? null);
      setDraft('');
    } catch (error) {
      appToast.apiError(error, t('support.chat.sendError'));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const firstName = useMemo(() => {
    const name = String(getStoredUser()?.name ?? '').trim();
    return name.split(/\s+/)[0] || '';
  }, []);

  const lastSeenMineId = useMemo(() => {
    const readAt = conversation?.support_last_read_at ? dayjs(conversation.support_last_read_at) : null;
    if (!readAt) return null;
    const seen = messages.filter((m) => m.sender === 'me' && !dayjs(m.created_at).isAfter(readAt));
    return seen.length ? seen[seen.length - 1].id : null;
  }, [conversation?.support_last_read_at, messages]);

  if (!authenticated) {
    return null;
  }

  const isRtl = language === 'ar';

  return (
    <div className={`support-chat${open ? ' support-chat--open' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {open ? (
        <section className="support-chat__panel" role="dialog" aria-label={t('support.chat.title')}>
          <header className="support-chat__header">
            <span className="support-chat__header-icon" aria-hidden="true">
              <MessageOutlined />
            </span>
            <div className="support-chat__header-text">
              <strong>{t('support.chat.title')}</strong>
              <span>{t('support.chat.subtitle')}</span>
            </div>
            <Button
              type="text"
              className="support-chat__close"
              aria-label={t('support.chat.close')}
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
            />
          </header>

          <div className="support-chat__messages" ref={listRef}>
            {loading ? (
              <div className="support-chat__loading"><Spin /></div>
            ) : (
              <>
                <div className="support-chat__bubble-row support-chat__bubble-row--support">
                  <div className="support-chat__bubble support-chat__bubble--support">
                    {firstName
                      ? t('support.chat.greetingNamed', { name: firstName })
                      : t('support.chat.greeting')}
                  </div>
                </div>
                {messages.map((m) => {
                  const mine = m.sender === 'me';
                  return (
                    <div
                      key={m.id}
                      className={`support-chat__bubble-row support-chat__bubble-row--${mine ? 'mine' : 'support'}`}
                    >
                      {!mine && m.author_name ? (
                        <span className="support-chat__author">{m.author_name}</span>
                      ) : null}
                      <div className={`support-chat__bubble support-chat__bubble--${mine ? 'mine' : 'support'}`}>
                        {m.body}
                      </div>
                      <span className="support-chat__meta">
                        {dayjs(m.created_at).format('DD MMM, HH:mm')}
                        {m.id === lastSeenMineId ? ` · ${t('support.chat.seen')}` : ''}
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <footer className="support-chat__composer">
            <Input.TextArea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('support.chat.placeholder')}
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={MAX_BODY_LENGTH}
              disabled={sending}
            />
            <Button
              type="primary"
              icon={<SendOutlined style={isRtl ? { transform: 'scaleX(-1)' } : undefined} />}
              aria-label={t('support.chat.send')}
              loading={sending}
              disabled={!draft.trim()}
              onClick={send}
            >
              {t('support.chat.send')}
            </Button>
          </footer>
        </section>
      ) : null}

      <Badge count={unreadCount} offset={isRtl ? [10, 4] : [-4, 4]}>
        <button
          type="button"
          className="support-chat__launcher"
          aria-label={open ? t('support.chat.close') : t('support.chat.open')}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? <CloseOutlined /> : <MessageOutlined />}
        </button>
      </Badge>
    </div>
  );
};

export default SupportChatWidget;
